/**
 * Reading and changing one of the signed-in seller's deals (canon APP-005, CORE-015).
 *
 * This is the one seller module whose path carries a value, and the exception is
 * deliberate. `contracts/API.md` addresses a deal as `GET|PATCH /v1/seller/deals/:dealId`,
 * so the id is part of the resource rather than a claim about who is asking:
 *
 *   - the id sent is always one **core returned for this session**, never a value read from
 *     the address bar, from storage or from anything a page could plant (canon APP-001);
 *   - it is percent-encoded, so a crafted id cannot escape the path segment it belongs to;
 *   - core binds ownership inside the statement it runs and answers `404` for a deal
 *     belonging to another founder, indistinguishably from one that does not exist. The id
 *     is therefore not a capability — knowing one buys nothing.
 *
 * No `sellerId` appears here, in a path or in a body. The session decides whose deal this
 * is, and core refuses an unknown field rather than ignoring it.
 *
 * **Only fields that actually changed are sent.** Core treats a field sent with the value it
 * already has as no change at all, but a form that PATCHed every input would still be
 * asking core to re-evaluate a deal the founder did not touch. Sending the difference is
 * the honest request, and it is what makes `changedFields` in the answer worth reading.
 */
import { apiRequest } from "./client";
import { parseSellerDeal } from "./desk";
import { INVALID, asRecord, malformed, nonEmptyString } from "./parse";
import type {
  DealEditOutcome,
  DealEditPatch,
  DealEditPolicy,
  EditableDealField,
  SavedDeal,
  SellerDeal,
  SellerDealDetail,
  SellerDealStatus,
  SellerDecision,
} from "./types";

/**
 * The field names this app understands, used to filter core's published lists.
 *
 * Core owns the classification; this set only decides which of its names this bundle can
 * draw a control for. A field core adds before this app learns to render it is dropped
 * rather than rejected — the editor then offers one control fewer, which is honest, where
 * refusing the whole response would take the editor away entirely.
 */
const EDITABLE_FIELDS = new Set<string>([
  "productName",
  "productUrl",
  "logoUrl",
  "shortDescription",
  "dealUrl",
  "dealType",
  "originalPriceCents",
  "dealPriceCents",
  "valueNotes",
  "couponCode",
  "limitations",
  "isExclusive",
]);

const DEAL_STATUSES = new Set<string>([
  "submitted",
  "changes_required",
  "approved",
  "rejected",
  "disabled",
]);

function isEditableField(value: string): value is EditableDealField {
  return EDITABLE_FIELDS.has(value);
}

/** A list of field names, or `INVALID` when it is not a list of strings at all. */
function parseFieldList(value: unknown): EditableDealField[] | typeof INVALID {
  if (!Array.isArray(value)) return INVALID;
  return value.filter(
    (entry): entry is EditableDealField =>
      typeof entry === "string" && isEditableField(entry),
  );
}

/**
 * Returns `null` when the body does not carry a usable edit policy.
 *
 * Every part of it is required. `editable`, `returnsToReview` and the two field lists are
 * what the editor uses to decide whether to offer a form at all, which controls to draw and
 * which pending change is worth warning about — a policy read half-way would produce an
 * editor that is confidently wrong about the consequence of saving, which is the one thing
 * this surface must never be.
 */
export function parseDealEditPolicy(value: unknown): DealEditPolicy | null {
  const row = asRecord(value);
  if (row === null) return null;

  if (typeof row.editable !== "boolean") return null;
  if (typeof row.returnsToReview !== "boolean") return null;

  const editableFields = parseFieldList(row.editableFields);
  const materialFields = parseFieldList(row.materialFields);
  if (editableFields === INVALID || materialFields === INVALID) return null;

  // `null` is the contract's own value for "no refusal", so it is not an absence.
  const refusalCode =
    row.refusalCode === null || row.refusalCode === undefined
      ? null
      : nonEmptyString(row.refusalCode);
  if (row.refusalCode !== null && row.refusalCode !== undefined && refusalCode === null) {
    return null;
  }

  return {
    editable: row.editable,
    refusalCode,
    editableFields,
    materialFields,
    returnsToReview: row.returnsToReview,
  };
}

const DECISION_ACTIONS = new Set<string>(["approve", "request_changes", "reject", "disable"]);

/**
 * One recorded decision (canon decision `D-016`).
 *
 * An entry this bundle cannot read is dropped rather than failing the whole detail: the
 * history is secondary to the outstanding note, and losing the edit form over one
 * unreadable past decision would cost the founder the thing they came to do. That is the
 * opposite trade from `parseSellerDeal`, where an unreadable deal means the review state on
 * screen would be a guess.
 */
function parseDecision(value: unknown): SellerDecision | null {
  const row = asRecord(value);
  if (row === null) return null;

  const action = typeof row.action === "string" ? row.action : null;
  const status = typeof row.status === "string" ? row.status : null;
  const decidedAt = typeof row.decidedAt === "string" ? row.decidedAt.trim() : "";
  if (action === null || status === null || decidedAt === "") return null;
  if (!DECISION_ACTIONS.has(action) || !DEAL_STATUSES.has(status)) return null;

  const note = typeof row.note === "string" && row.note.trim() !== "" ? row.note : null;
  return {
    action: action as SellerDecision["action"],
    status: status as SellerDealStatus,
    note,
    decidedAt,
  };
}

/**
 * Returns `null` when the body is not a deal with its edit policy.
 *
 * An absent `decisions` key reads as an empty history rather than a malformed body. A deal
 * moderated before core recorded these genuinely has none (`D-016` § 5 refuses to backfill),
 * and that state must render.
 */
export function parseSellerDealDetail(body: unknown): SellerDealDetail | null {
  const row = asRecord(body);
  if (row === null) return null;

  const deal = parseSellerDeal(row.deal);
  const editPolicy = parseDealEditPolicy(row.editPolicy);
  if (deal === null || editPolicy === null) return null;

  const decisions = Array.isArray(row.decisions)
    ? row.decisions.flatMap((entry) => {
        const decision = parseDecision(entry);
        return decision === null ? [] : [decision];
      })
    : [];

  return { deal, editPolicy, decisions };
}

/**
 * Returns `null` when the body is not core's report of an accepted edit.
 *
 * `returnedToReview` is required rather than defaulted to `false`. It is the fact a founder
 * must be told — that their deal has left the public board and cannot bid until it is
 * approved again — and a missing value read as "no" would suppress exactly that sentence.
 */
export function parseDealEditOutcome(value: unknown): DealEditOutcome | null {
  const row = asRecord(value);
  if (row === null) return null;

  const changedFields = parseFieldList(row.changedFields);
  const materialFields = parseFieldList(row.materialFields);
  if (changedFields === INVALID || materialFields === INVALID) return null;
  if (typeof row.returnedToReview !== "boolean") return null;

  const fromStatus = nonEmptyString(row.fromStatus);
  if (fromStatus === null || !DEAL_STATUSES.has(fromStatus)) return null;

  return {
    changedFields,
    materialFields,
    returnedToReview: row.returnedToReview,
    fromStatus: fromStatus as SellerDealStatus,
  };
}

/** Returns `null` when the body is not an accepted edit. */
export function parseSavedDeal(body: unknown): SavedDeal | null {
  const row = asRecord(body);
  if (row === null) return null;

  const deal = parseSellerDeal(row.deal);
  const edit = parseDealEditOutcome(row.edit);
  const editPolicy = parseDealEditPolicy(row.editPolicy);
  if (deal === null || edit === null || editPolicy === null) return null;

  return { deal, edit, editPolicy };
}

/** The path for one deal, with the id core returned encoded into its own segment. */
function dealPath(dealId: string): string {
  return `/v1/seller/deals/${encodeURIComponent(dealId)}`;
}

/**
 * Re-reads one deal together with the edit rule that applies to it.
 *
 * The list read carries no policy, and the editor needs one: `returnsToReview` depends on
 * the deal's status, which can change between the desk loading and a founder deciding to
 * edit. Reading again when the editor opens is what keeps the warning it shows true.
 */
export async function fetchSellerDealDetail(dealId: string): Promise<SellerDealDetail> {
  const detail = parseSellerDealDetail(await apiRequest<unknown>(dealPath(dealId)));
  if (detail === null) throw malformed();
  return detail;
}

/**
 * Applies one edit to one of the signed-in seller's deals.
 *
 * The patch is exactly what changed. Core validates the merged deal, decides whether the
 * edit is material, decides where that leaves the deal's status, and answers with what it
 * did — none of which this app predicts, and all of which it reports from the response.
 */
export async function saveSellerDeal(input: {
  dealId: string;
  patch: DealEditPatch;
}): Promise<SavedDeal> {
  const body = await apiRequest<unknown>(dealPath(input.dealId), {
    method: "PATCH",
    body: input.patch,
  });
  const saved = parseSavedDeal(body);
  if (saved === null) throw malformed();
  return saved;
}

/**
 * The editable half of a deal, as this app holds it between core's projection and its form.
 *
 * Core's seller projection does not carry `isExclusive` as its own field: canon § 4 makes
 * the Exclusive badge the published form of that fact, so it arrives in `badges`. Reading
 * it back from there is the same value core stores — `badgesFor` sets the badge from
 * `is_exclusive` and from nothing else — rather than a guess about what the founder meant.
 */
export interface EditableDealValues {
  productName: string;
  productUrl: string;
  logoUrl: string | null;
  shortDescription: string;
  dealUrl: string;
  dealType: SellerDeal["dealType"];
  originalPriceCents: number | null;
  dealPriceCents: number | null;
  valueNotes: string | null;
  couponCode: string | null;
  limitations: string | null;
  isExclusive: boolean;
}

export function editableValuesOf(deal: SellerDeal): EditableDealValues {
  return {
    productName: deal.productName,
    productUrl: deal.productUrl ?? "",
    logoUrl: deal.logoUrl,
    shortDescription: deal.shortDescription ?? "",
    dealUrl: deal.dealUrl ?? "",
    dealType: deal.dealType,
    originalPriceCents: deal.originalPriceCents,
    dealPriceCents: deal.dealPriceCents,
    valueNotes: deal.valueNotes,
    couponCode: deal.couponCode,
    limitations: deal.limitations,
    isExclusive: deal.badges.includes("exclusive"),
  };
}
